import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import {
  Settings,
  Save,
  RefreshCw,
  Shield,
  Bell,
  Database,
  Zap,
  Globe,
  Lock,
  AlertTriangle,
  CheckCircle
} from 'lucide-react'
import toast from 'react-hot-toast'
import { unifiedAdminApiClient as adminApi } from '../services/unified-admin-api-client'

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState('general')
  const queryClient = useQueryClient()

  const { data: systemConfigData, isLoading, error, refetch } = useQuery(
    'system-config',
    () => adminApi.getSystemConfig(),
    {
      retry: 3,
      retryDelay: 1000,
      onError: (error: any) => {
        console.error('Failed to load system configuration:', error)
        toast.error('Failed to load system configuration. Please try again.')
      }
    }
  )

  const updateConfigMutation = useMutation(
    (config: any) => adminApi.updateSystemConfig(config),
    {
      onSuccess: () => {
        toast.success('Settings updated successfully')
        queryClient.invalidateQueries('system-config')
      },
      onError: () => {
        toast.error('Failed to update settings')
      },
    }
  )

  const toggleMaintenanceMutation = useMutation(
    (enabled: boolean) => adminApi.toggleMaintenanceMode(enabled),
    {
      onSuccess: () => {
        toast.success('Maintenance mode updated')
        queryClient.invalidateQueries('system-config')
      },
      onError: () => {
        toast.error('Failed to update maintenance mode')
      },
    }
  )

  const systemConfig = systemConfigData?.data

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-3 text-gray-600">Loading system configuration...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-red-800 mb-2">Failed to Load Configuration</h3>
          <p className="text-red-600 mb-4">
            Unable to load system configuration. This might be due to an expired session or server error.
          </p>
          <button
            onClick={() => refetch()}
            className="btn-primary"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            System Settings
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Configure system-wide settings and preferences
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('general')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'general'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Settings className="inline h-4 w-4 mr-2" />
            General
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'security'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Shield className="inline h-4 w-4 mr-2" />
            Security
          </button>
          <button
            onClick={() => setActiveTab('notifications')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'notifications'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Bell className="inline h-4 w-4 mr-2" />
            Notifications
          </button>
          <button
            onClick={() => setActiveTab('integrations')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'integrations'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Globe className="inline h-4 w-4 mr-2" />
            Integrations
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'general' && (
        <GeneralSettingsTab 
          config={systemConfig} 
          onUpdate={(config) => updateConfigMutation.mutate(config)}
          onToggleMaintenance={(enabled) => toggleMaintenanceMutation.mutate(enabled)}
          isLoading={updateConfigMutation.isLoading || toggleMaintenanceMutation.isLoading}
        />
      )}
      {activeTab === 'security' && <SecuritySettingsTab config={systemConfig} />}
      {activeTab === 'notifications' && <NotificationSettingsTab config={systemConfig} />}
      {activeTab === 'integrations' && <IntegrationsTab config={systemConfig} />}
    </div>
  )
}

interface GeneralSettingsTabProps {
  config: any
  onUpdate: (config: any) => void
  onToggleMaintenance: (enabled: boolean) => void
  isLoading: boolean
}

function GeneralSettingsTab({ config, onUpdate, onToggleMaintenance, isLoading }: GeneralSettingsTabProps) {
  const [settings, setSettings] = useState({
    maintenance_mode: config?.maintenance_mode || false,
    max_file_size_mb: config?.max_file_size_mb || 10,
    session_timeout_hours: config?.session_timeout_hours || 24,
    max_concurrent_generations: config?.max_concurrent_generations || 100,
  })

  // Sync settings with backend data when config changes
  React.useEffect(() => {
    if (config) {
      setSettings({
        maintenance_mode: config.maintenance_mode || false,
        max_file_size_mb: config.max_file_size_mb || 10,
        session_timeout_hours: config.session_timeout_hours || 24,
        max_concurrent_generations: config.max_concurrent_generations || 100,
      })
    }
  }, [config])

  const handleSave = () => {
    // Map frontend field names to backend field names
    const backendSettings = {
      maintenance_mode: settings.maintenance_mode,
      max_file_size_mb: settings.max_file_size_mb,
      session_timeout_hours: settings.session_timeout_hours,
      max_concurrent_generations: settings.max_concurrent_generations,
    }
    onUpdate(backendSettings)
  }

  return (
    <div className="space-y-6">
      {/* Maintenance Mode */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">Maintenance Mode</h3>
            <div className="flex items-center space-x-2">
              {settings.maintenance_mode ? (
                <AlertTriangle className="h-5 w-5 text-red-500" />
              ) : (
                <CheckCircle className="h-5 w-5 text-green-500" />
              )}
              <span className={`text-sm font-medium ${
                settings.maintenance_mode ? 'text-red-600' : 'text-green-600'
              }`}>
                {settings.maintenance_mode ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>
        </div>
        <div className="card-body">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">
                When enabled, the system will be in maintenance mode and users will see a maintenance page.
              </p>
            </div>
            <button
              onClick={() => {
                const newValue = !settings.maintenance_mode
                setSettings({ ...settings, maintenance_mode: newValue })
                onToggleMaintenance(newValue)
              }}
              disabled={isLoading}
              className={`btn ${settings.maintenance_mode ? 'btn-danger' : 'btn-secondary'}`}
            >
              {settings.maintenance_mode ? 'Disable' : 'Enable'} Maintenance Mode
            </button>
          </div>
        </div>
      </div>

      {/* System Limits */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium">System Limits</h3>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label">Max Upload Size (MB)</label>
              <input
                type="number"
                className="input"
                value={settings.max_file_size_mb}
                onChange={(e) => setSettings({ ...settings, max_file_size_mb: parseInt(e.target.value) })}
                min="1"
                max="100"
              />
              <p className="text-xs text-gray-500 mt-1">Maximum file size for image uploads</p>
            </div>

            <div>
              <label className="label">Session Timeout (hours)</label>
              <input
                type="number"
                className="input"
                value={settings.session_timeout_hours}
                onChange={(e) => setSettings({ ...settings, session_timeout_hours: parseInt(e.target.value) })}
                min="1"
                max="168"
              />
              <p className="text-xs text-gray-500 mt-1">How long user sessions remain active</p>
            </div>

            <div>
              <label className="label">Max Generations per Hour</label>
              <input
                type="number"
                className="input"
                value={settings.max_concurrent_generations}
                onChange={(e) => setSettings({ ...settings, max_concurrent_generations: parseInt(e.target.value) })}
                min="1"
                max="1000"
              />
              <p className="text-xs text-gray-500 mt-1">Rate limit for AI generations per user</p>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="btn-primary"
            >
              <Save className="h-4 w-4 mr-2" />
              {isLoading ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>

      {/* Feature Flags */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium">Feature Flags</h3>
        </div>
        <div className="card-body">
          <div className="space-y-4">
            {[
              { 
                key: 'analytics_enabled', 
                label: 'Analytics Dashboard', 
                description: 'Enable analytics and reporting features',
                checked: config?.enable_analytics || false
              },
              { 
                key: 'batch_processing', 
                label: 'Batch Processing', 
                description: 'Allow users to process multiple images at once',
                checked: true // Default enabled for production
              },
              { 
                key: 'mls_export', 
                label: 'MLS Export', 
                description: 'Enable MLS-compliant export functionality',
                checked: true // Default enabled for production
              },
              { 
                key: 'watermarks', 
                label: 'Watermark System', 
                description: 'Allow users to add watermarks to generated images',
                checked: true // Default enabled for production
              },
            ].map((feature) => (
              <div key={feature.key} className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900">{feature.label}</h4>
                  <p className="text-sm text-gray-500">{feature.description}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={feature.checked}
                    onChange={(e) => {
                      // Update feature flag in backend
                      const updatedConfig = { ...config }
                      if (feature.key === 'analytics_enabled') {
                        updatedConfig.enable_analytics = e.target.checked
                      }
                      onUpdate(updatedConfig)
                    }}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function SecuritySettingsTab({ config }: { config: any }) {
  const [securityConfig, setSecurityConfig] = React.useState(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const [isSaving, setIsSaving] = React.useState(false)
  
  const [settings, setSettings] = React.useState({
    password_min_length: 8,
    password_require_uppercase: true,
    password_require_lowercase: true,
    password_require_numbers: true,
    password_require_special: false,
    session_require_https: true,
    session_secure_cookies: true,
    session_ip_validation: false,
    max_login_attempts: 5,
    account_lockout_duration_minutes: 30,
  })

  // Load security configuration
  React.useEffect(() => {
    const loadSecurityConfig = async () => {
      try {
        setIsLoading(true)
        const response = await adminApi.getSecurityConfig()
        if (response.success && response.data) {
          setSecurityConfig(response.data)
          setSettings(response.data)
        }
      } catch (error) {
        console.error('Failed to load security config:', error)
        toast.error('Failed to load security configuration')
      } finally {
        setIsLoading(false)
      }
    }

    loadSecurityConfig()
  }, [])

  const handleSave = async () => {
    try {
      setIsSaving(true)
      const response = await adminApi.updateSecurityConfig(settings)
      if (response.success) {
        toast.success('Security settings updated successfully')
        setSecurityConfig(response.data)
      } else {
        toast.error(response.message || 'Failed to update security settings')
      }
    } catch (error) {
      console.error('Failed to update security config:', error)
      toast.error('Failed to update security settings')
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-3 text-gray-600">Loading security configuration...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium">Password Policy</h3>
        </div>
        <div className="card-body">
          <div className="space-y-6">
            <div>
              <label className="label">Minimum Password Length</label>
              <input
                type="number"
                className="input w-32"
                value={settings.password_min_length}
                onChange={(e) => setSettings({ ...settings, password_min_length: parseInt(e.target.value) })}
                min="6"
                max="32"
              />
              <p className="text-xs text-gray-500 mt-1">Minimum number of characters required</p>
            </div>

            <div>
              <label className="label">Password Requirements</label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input 
                    type="checkbox" 
                    className="rounded border-gray-300" 
                    checked={settings.password_require_uppercase}
                    onChange={(e) => setSettings({ ...settings, password_require_uppercase: e.target.checked })}
                  />
                  <span className="ml-2 text-sm">Require uppercase letters</span>
                </label>
                <label className="flex items-center">
                  <input 
                    type="checkbox" 
                    className="rounded border-gray-300" 
                    checked={settings.password_require_lowercase}
                    onChange={(e) => setSettings({ ...settings, password_require_lowercase: e.target.checked })}
                  />
                  <span className="ml-2 text-sm">Require lowercase letters</span>
                </label>
                <label className="flex items-center">
                  <input 
                    type="checkbox" 
                    className="rounded border-gray-300" 
                    checked={settings.password_require_numbers}
                    onChange={(e) => setSettings({ ...settings, password_require_numbers: e.target.checked })}
                  />
                  <span className="ml-2 text-sm">Require numbers</span>
                </label>
                <label className="flex items-center">
                  <input 
                    type="checkbox" 
                    className="rounded border-gray-300" 
                    checked={settings.password_require_special}
                    onChange={(e) => setSettings({ ...settings, password_require_special: e.target.checked })}
                  />
                  <span className="ml-2 text-sm">Require special characters</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium">Session Security</h3>
        </div>
        <div className="card-body">
          <div className="space-y-4">
            <label className="flex items-center">
              <input 
                type="checkbox" 
                className="rounded border-gray-300" 
                checked={settings.session_require_https}
                onChange={(e) => setSettings({ ...settings, session_require_https: e.target.checked })}
              />
              <span className="ml-2 text-sm">Require HTTPS</span>
            </label>
            <label className="flex items-center">
              <input 
                type="checkbox" 
                className="rounded border-gray-300" 
                checked={settings.session_secure_cookies}
                onChange={(e) => setSettings({ ...settings, session_secure_cookies: e.target.checked })}
              />
              <span className="ml-2 text-sm">Secure cookies</span>
            </label>
            <label className="flex items-center">
              <input 
                type="checkbox" 
                className="rounded border-gray-300" 
                checked={settings.session_ip_validation}
                onChange={(e) => setSettings({ ...settings, session_ip_validation: e.target.checked })}
              />
              <span className="ml-2 text-sm">IP address validation</span>
            </label>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium">Account Protection</h3>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label">Max Login Attempts</label>
              <input
                type="number"
                className="input"
                value={settings.max_login_attempts}
                onChange={(e) => setSettings({ ...settings, max_login_attempts: parseInt(e.target.value) })}
                min="1"
                max="20"
              />
              <p className="text-xs text-gray-500 mt-1">Number of failed attempts before lockout</p>
            </div>

            <div>
              <label className="label">Lockout Duration (minutes)</label>
              <input
                type="number"
                className="input"
                value={settings.account_lockout_duration_minutes}
                onChange={(e) => setSettings({ ...settings, account_lockout_duration_minutes: parseInt(e.target.value) })}
                min="1"
                max="1440"
              />
              <p className="text-xs text-gray-500 mt-1">How long accounts remain locked</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button 
          onClick={handleSave}
          disabled={isSaving}
          className="btn-primary"
        >
          <Save className="h-4 w-4 mr-2" />
          {isSaving ? 'Saving...' : 'Save Security Settings'}
        </button>
      </div>
    </div>
  )
}

function NotificationSettingsTab({ config }: { config: any }) {
  const [notificationConfig, setNotificationConfig] = React.useState(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const [isSaving, setIsSaving] = React.useState(false)
  
  const [settings, setSettings] = React.useState({
    notification_email: "admin@virtualrevamp.com",
    notify_user_registration: true,
    notify_system_errors: true,
    notify_high_usage: true,
    notify_security_alerts: true,
    email_smtp_host: "",
    email_smtp_port: 587,
    email_smtp_username: "",
    email_smtp_password: "",
    email_smtp_use_tls: true,
  })

  // Load notification configuration
  React.useEffect(() => {
    const loadNotificationConfig = async () => {
      try {
        setIsLoading(true)
        const response = await adminApi.getNotificationConfig()
        if (response.success && response.data) {
          setNotificationConfig(response.data)
          setSettings(prev => ({ ...prev, ...response.data }))
        }
      } catch (error) {
        console.error('Failed to load notification config:', error)
        toast.error('Failed to load notification configuration')
      } finally {
        setIsLoading(false)
      }
    }

    loadNotificationConfig()
  }, [])

  const handleSave = async () => {
    try {
      setIsSaving(true)
      const response = await adminApi.updateNotificationConfig(settings)
      if (response.success) {
        toast.success('Notification settings updated successfully')
        setNotificationConfig(response.data)
      } else {
        toast.error(response.message || 'Failed to update notification settings')
      }
    } catch (error) {
      console.error('Failed to update notification config:', error)
      toast.error('Failed to update notification settings')
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-3 text-gray-600">Loading notification configuration...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium">Email Notifications</h3>
        </div>
        <div className="card-body">
          <div className="space-y-4">
            {[
              { 
                key: 'notify_user_registration', 
                label: 'New User Registration', 
                description: 'Notify when new users register',
                checked: settings.notify_user_registration
              },
              { 
                key: 'notify_system_errors', 
                label: 'System Errors', 
                description: 'Notify when critical errors occur',
                checked: settings.notify_system_errors,
                disabled: true // Always enabled for production
              },
              { 
                key: 'notify_high_usage', 
                label: 'High Usage Alerts', 
                description: 'Notify when system usage is high',
                checked: settings.notify_high_usage
              },
              { 
                key: 'notify_security_alerts', 
                label: 'Security Alerts', 
                description: 'Notify about security events',
                checked: settings.notify_security_alerts,
                disabled: true // Always enabled for production
              },
            ].map((notification) => (
              <div key={notification.key} className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900">{notification.label}</h4>
                  <p className="text-sm text-gray-500">{notification.description}</p>
                  {notification.disabled && (
                    <p className="text-xs text-blue-600">Required for production security</p>
                  )}
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={notification.checked}
                    disabled={notification.disabled}
                    onChange={(e) => {
                      if (!notification.disabled) {
                        setSettings(prev => ({
                          ...prev,
                          [notification.key]: e.target.checked
                        }))
                      }
                    }}
                  />
                  <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 ${notification.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}></div>
                </label>
              </div>
            ))}
          </div>

          <div className="mt-6">
            <label className="label">Notification Email</label>
            <input
              type="email"
              className="input"
              placeholder="admin@example.com"
              value={settings.notification_email}
              onChange={(e) => setSettings({ ...settings, notification_email: e.target.value })}
            />
            <p className="text-xs text-gray-500 mt-1">Email address to receive admin notifications</p>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium">SMTP Configuration</h3>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label">SMTP Host</label>
              <input
                type="text"
                className="input"
                placeholder="smtp.gmail.com"
                value={settings.email_smtp_host}
                onChange={(e) => setSettings({ ...settings, email_smtp_host: e.target.value })}
              />
            </div>

            <div>
              <label className="label">SMTP Port</label>
              <input
                type="number"
                className="input"
                placeholder="587"
                value={settings.email_smtp_port}
                onChange={(e) => setSettings({ ...settings, email_smtp_port: parseInt(e.target.value) })}
              />
            </div>

            <div>
              <label className="label">SMTP Username</label>
              <input
                type="text"
                className="input"
                placeholder="your-email@gmail.com"
                value={settings.email_smtp_username}
                onChange={(e) => setSettings({ ...settings, email_smtp_username: e.target.value })}
              />
            </div>

            <div>
              <label className="label">SMTP Password</label>
              <input
                type="password"
                className="input"
                placeholder="••••••••"
                value={settings.email_smtp_password}
                onChange={(e) => setSettings({ ...settings, email_smtp_password: e.target.value })}
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                className="rounded border-gray-300"
                checked={settings.email_smtp_use_tls}
                onChange={(e) => setSettings({ ...settings, email_smtp_use_tls: e.target.checked })}
              />
              <span className="ml-2 text-sm">Use TLS/SSL encryption</span>
            </label>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button 
          onClick={handleSave}
          disabled={isSaving}
          className="btn-primary"
        >
          <Save className="h-4 w-4 mr-2" />
          {isSaving ? 'Saving...' : 'Save Notification Settings'}
        </button>
      </div>
    </div>
  )
}

function IntegrationsTab({ config }: { config: any }) {
  const [integrationConfig, setIntegrationConfig] = React.useState(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const [isSaving, setIsSaving] = React.useState(false)
  
  const [settings, setSettings] = React.useState({
    comfyui_endpoint: "http://localhost:8188",
    supabase_url: "",
    supabase_key: "",
    stripe_webhook_secret: "",
    cloudflare_api_key: "",
    runpod_api_key: "",
    webhook_endpoints: [] as string[],
  })

  // Load integration configuration
  React.useEffect(() => {
    const loadIntegrationConfig = async () => {
      try {
        setIsLoading(true)
        const response = await adminApi.getIntegrationConfig()
        if (response.success && response.data) {
          setIntegrationConfig(response.data)
          setSettings(prev => ({ ...prev, ...response.data }))
        }
      } catch (error) {
        console.error('Failed to load integration config:', error)
        toast.error('Failed to load integration configuration')
      } finally {
        setIsLoading(false)
      }
    }

    loadIntegrationConfig()
  }, [])

  const handleSave = async () => {
    try {
      setIsSaving(true)
      const response = await adminApi.updateIntegrationConfig(settings)
      if (response.success) {
        toast.success('Integration settings updated successfully')
        setIntegrationConfig(response.data)
      } else {
        toast.error(response.message || 'Failed to update integration settings')
      }
    } catch (error) {
      console.error('Failed to update integration config:', error)
      toast.error('Failed to update integration settings')
    } finally {
      setIsSaving(false)
    }
  }

  const addWebhookEndpoint = () => {
    setSettings(prev => ({
      ...prev,
      webhook_endpoints: [...prev.webhook_endpoints, ""]
    }))
  }

  const updateWebhookEndpoint = (index: number, value: string) => {
    setSettings(prev => ({
      ...prev,
      webhook_endpoints: prev.webhook_endpoints.map((endpoint, i) => 
        i === index ? value : endpoint
      )
    }))
  }

  const removeWebhookEndpoint = (index: number) => {
    setSettings(prev => ({
      ...prev,
      webhook_endpoints: prev.webhook_endpoints.filter((_, i) => i !== index)
    }))
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-3 text-gray-600">Loading integration configuration...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium">AI Processing</h3>
        </div>
        <div className="card-body">
          <div className="space-y-4">
            <div>
              <label className="label">ComfyUI Endpoint</label>
              <input
                type="url"
                className="input"
                placeholder="http://localhost:8188"
                value={settings.comfyui_endpoint}
                onChange={(e) => setSettings({ ...settings, comfyui_endpoint: e.target.value })}
              />
              <p className="text-xs text-gray-500 mt-1">URL for ComfyUI AI processing service</p>
            </div>

            <div>
              <label className="label">RunPod API Key</label>
              <input
                type="password"
                className="input"
                placeholder="••••••••••••••••"
                value={settings.runpod_api_key}
                onChange={(e) => setSettings({ ...settings, runpod_api_key: e.target.value })}
              />
              <p className="text-xs text-gray-500 mt-1">API key for RunPod serverless AI processing</p>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium">Payment Processing</h3>
        </div>
        <div className="card-body">
          <div>
            <label className="label">Stripe Webhook Secret</label>
            <input
              type="password"
              className="input"
              placeholder="whsec_..."
              value={settings.stripe_webhook_secret}
              onChange={(e) => setSettings({ ...settings, stripe_webhook_secret: e.target.value })}
            />
            <p className="text-xs text-gray-500 mt-1">Webhook endpoint secret for Stripe payment events</p>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium">Cloud Storage</h3>
        </div>
        <div className="card-body">
          <div>
            <label className="label">Cloudflare R2 API Key</label>
            <input
              type="password"
              className="input"
              placeholder="••••••••••••••••"
              value={settings.cloudflare_api_key}
              onChange={(e) => setSettings({ ...settings, cloudflare_api_key: e.target.value })}
            />
            <p className="text-xs text-gray-500 mt-1">API key for Cloudflare R2 object storage</p>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium">Database & Authentication</h3>
        </div>
        <div className="card-body">
          <div className="space-y-4">
            <div>
              <label className="label">Supabase Project URL</label>
              <input
                type="url"
                className="input"
                placeholder="https://your-project.supabase.co"
                value={settings.supabase_url}
                onChange={(e) => setSettings({ ...settings, supabase_url: e.target.value })}
              />
            </div>
            <div>
              <label className="label">Supabase Service Role Key</label>
              <input
                type="password"
                className="input"
                placeholder="••••••••••••••••"
                value={settings.supabase_key}
                onChange={(e) => setSettings({ ...settings, supabase_key: e.target.value })}
              />
              <p className="text-xs text-gray-500 mt-1">Service role key for server-side operations</p>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">Webhook Endpoints</h3>
            <button
              type="button"
              onClick={addWebhookEndpoint}
              className="btn-secondary text-sm"
            >
              Add Endpoint
            </button>
          </div>
        </div>
        <div className="card-body">
          {settings.webhook_endpoints.length === 0 ? (
            <p className="text-gray-500 text-sm">No webhook endpoints configured</p>
          ) : (
            <div className="space-y-3">
              {settings.webhook_endpoints.map((endpoint, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <input
                    type="url"
                    className="input flex-1"
                    placeholder="https://your-webhook-endpoint.com/webhook"
                    value={endpoint}
                    onChange={(e) => updateWebhookEndpoint(index, e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => removeWebhookEndpoint(index)}
                    className="btn-danger text-sm"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-gray-500 mt-2">External webhook endpoints to notify of system events</p>
        </div>
      </div>

      <div className="flex justify-end">
        <button 
          onClick={handleSave}
          disabled={isSaving}
          className="btn-primary"
        >
          <Save className="h-4 w-4 mr-2" />
          {isSaving ? 'Saving...' : 'Save Integration Settings'}
        </button>
      </div>
    </div>
  )
}